import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subject, Subscription} from 'rxjs';

import {
    ApiError,
    ServerResponseData,
    HttpStatusCodesEnum,
    PaymentMethodsEnum,
    TradingAccountStatusesEnum,
    AuthApiService,
    PaymentsService,
    ReportsService,
    CashAccountDetailedReportInterface,
    TradingAccountDetailedReportInterface,
    LinkedAccountModel,
    TradingAccountModel,
    CashAccountDetailedReportModel,
    CashAccountDetailedFilterModel,
    TradingAccountDetailedFilterModel,
    TradingAccountDetailedReportModel,
    IAccountCashBalancesResultItem, ReportingNetService
} from '@spa/api';

import {FormComponent} from '../../../../shared/widgets';
import {AlertsService} from '../../../../shared/services';
import {FormInterface, PermissionRuleInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components';

import {WithdrawalForm} from '../../forms';

import {WithdrawalFormErrorStatesEnum} from '../../enums/error-states';

import {WithdrawalMessages} from '../../../../../assets/messages';
import {PanelAccountsEditTrait} from 'appEntries/panel/traits';
import {EventsService} from 'appEntries/panel/services';

@Component({
    selector: 'app-withdrawal',
    templateUrl: './withdrawal.component.html',
    styleUrls: ['./withdrawal.component.scss']
})
export class WithdrawalComponent extends DialogComponent<WithdrawalComponent, boolean>
    implements FormInterface, OnDestroy {

    private getAccountToDetailsSubscription: Subscription;
    private registerWithdrawalSubscription: Subscription;

    public paymentMethod: number;
    public visibleTransferButton: boolean;
    public withdrawalForm: WithdrawalForm;
    public paymentMethods: typeof PaymentMethodsEnum;
    public tradingAccountStatuses: typeof TradingAccountStatusesEnum;
    public accountTo: {id: number, balance: IAccountCashBalancesResultItem};

    constructor(
        private readonly authenticationService: AuthApiService,
        private paymentsService: PaymentsService,
        private reportsService: ReportingNetService,
        private alertService: AlertsService,
        private eventService: EventsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            balanceData: IAccountCashBalancesResultItem,
            tradingAccounts: TradingAccountModel[],
            linkedAccounts: LinkedAccountModel[],
            config: DialogConfigInterface,
            transferPermissionRule: PermissionRuleInterface,
            withdrawalPermissionRule: PermissionRuleInterface,
        },
        public dialogRef: MatDialogRef<WithdrawalComponent>
    ) {
        super(dialogRef);
        this.config = data.config;
        this.paymentMethods = PaymentMethodsEnum;
        this.tradingAccountStatuses = TradingAccountStatusesEnum;

        this.visibleTransferButton = PanelAccountsEditTrait.checkVisibilityTransferButton(
            this.data.account.AccountSubType,
            this.authenticationService.getAuthenticatedUser(),
            this.data.transferPermissionRule
        );
    }

    private getAccountRequestSubscriber(
        referenceId: string,
        baseCurrency: string,
        refreshCurrent: boolean,
        response: ServerResponseData<IAccountCashBalancesResultItem[]>
    ): boolean {
        if ((response.status !== HttpStatusCodesEnum.Ok) || (response.data === null)) {
            this.alertService.addAlert(WithdrawalMessages.CashBalancesFailed.message);

            return false;
        }
        let model = null;
        if (!response.data) {
            model = [{
                accountReferenceId : referenceId,
                cashBalances: [{
                    currency: baseCurrency,
                    availableCash: 0,
                    netCash: 0
                }]
            } as IAccountCashBalancesResultItem];
        }else{
            model = response.data as IAccountCashBalancesResultItem[];
        }

        if (!refreshCurrent) {
            this.accountTo.balance = model[0];
        } else {
            this.data.balanceData = model[0];
            this.withdrawalForm.balanceData = this.data.balanceData;
            this.withdrawalForm.accountBalanceChanged(this.data.account.accountId);
        }

    }

    selectPaymentMethod(method: number): void {
        this.initForm(method);

        const tradingAccountsCollectionLength = this.data.tradingAccounts.length;

        if (
            (method === PaymentMethodsEnum.InternalTransfer) &&
            ([0, 1].indexOf(tradingAccountsCollectionLength) < 0)
        ) {
            this.paymentMethod = method;
            this.data.config.buttonsHidden = false;
        }

        if (
            (method === PaymentMethodsEnum.BankTransfer) &&
            (this.data.linkedAccounts.length > 0) &&
            (this.data.account.accountStatus === TradingAccountStatusesEnum.Active)
        ) {
            this.paymentMethod = method;
            this.data.config.buttonsHidden = false;
        }
    }

    ngOnDestroy(): void {
        if (this.getAccountToDetailsSubscription) {
            this.getAccountToDetailsSubscription.unsubscribe();
        }

        if (this.registerWithdrawalSubscription) {
            this.registerWithdrawalSubscription.unsubscribe();
        }
    }

    getAccountDetails(accountId: number, refreshCurrent?: boolean): void {
        if (accountId) {
            let baseCurrency = null;
            let accountReferenceId = null;
            if (!refreshCurrent) {
                const filteredAccounts = this.data.tradingAccounts.filter((account: TradingAccountModel) => {
                    return account.id === accountId;
                });

                if (filteredAccounts.length > 0) {
                    baseCurrency = filteredAccounts[0].currencyCode;
                    accountReferenceId = filteredAccounts[0].accountReferenceId;
                }
                this.accountTo = {id : accountId, balance: null};
            } else {
                baseCurrency = this.data.account.currencyCode;
                accountReferenceId = this.data.account.accountReferenceId;
            }

            if (accountReferenceId) {
                const request = this.reportsService.getCrmAccountsCashBalancesMultiCurrency([accountReferenceId]);
                this.getAccountToDetailsSubscription = request
                    .subscribe(this.getAccountRequestSubscriber.bind(this, accountReferenceId, baseCurrency, refreshCurrent));
            }
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        const unavailableCashMessage = WithdrawalMessages.UnavailableAccountFromCash.message;
        const notEnoughMoneyMessage = WithdrawalMessages.AccountFromNotEnoughMoney.message;

        return {
            ...FormComponent.getFormMessages(controlLabel, controlMaxLength),
            [WithdrawalMessages.UnavailableAccountFromCash.key]: unavailableCashMessage,
            [WithdrawalMessages.AccountFromNotEnoughMoney.key]: notEnoughMoneyMessage
        };
    }

    initForm(method?: number): void {
        this.withdrawalForm = new WithdrawalForm(
            this.data.account,
            this.data.linkedAccounts,
            this.data.tradingAccounts,
            this.paymentsService,
            this.data.balanceData
        );

        this.withdrawalForm.initForm(method);
    }

    validateForm(): boolean {
        return this.withdrawalForm.validateForm(
            this.getFormMessages.bind(this),
            this.paymentMethod,
            WithdrawalMessages.UnavailableAccountFromCash.key,
            WithdrawalMessages.AccountFromNotEnoughMoney.key
        );
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.registerWithdrawalSubscription = this.withdrawalForm.createRequest(this.paymentMethod)
                            .subscribe((response: ServerResponseData<boolean>) => {
                                if (response.status !== HttpStatusCodesEnum.Ok) {
                                    this.config.process.failedMessage = (response.data as ApiError[])
                                        .map((error: ApiError) => {
                                            switch (error.status) {
                                                case WithdrawalFormErrorStatesEnum.RegisterBankWithdrawalFailed:
                                                    return WithdrawalMessages.RegisterBankWithdrawalFailed.message;
                                                case WithdrawalFormErrorStatesEnum.RegisterInternalWithdrawalFailed:
                                                    return WithdrawalMessages.RegisterInternalWithdrawalFailed.message;
                                                case WithdrawalFormErrorStatesEnum.CanFindCorrectPaymentMethod:
                                                    return WithdrawalMessages.CanFindCorrectPaymentMethod.message;
                                            }
                                        }).join('<br>');

                                    this.config.process.failed = true;
                                } else {
                                    this.eventService.forceCashBalance.next(this.data.account.accountReferenceId);
                                    switch (this.paymentMethod) {
                                        case PaymentMethodsEnum.BankTransfer:
                                            this.config.process.succeedMessage =
                                                WithdrawalMessages.RegisterBankWithdrawalSucceed.message;
                                            break;
                                        case PaymentMethodsEnum.InternalTransfer:
                                            this.config.process.succeedMessage =
                                                WithdrawalMessages.RegisterInternalWithdrawalSucceed.message;
                                            break;
                                    }
                                }

                                this.config.process.loading = false;
                                this.config.process.finished = true;
                            });
                    });
                }
                break;
        }
    }
}
